import { useCallback, useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { getItemLocal, setItemLocal } from "@miplatform/utils";

export const useSpecAreaFormViewModel = ({
  stateManager,
  handleAddAreas,
  handleRemoveFloor,
  planDetails,
}) => {
  // UI & form state
  const [showAreaDrawer, setShowAreaDrawer] = useState(false);
  const [areaData, setAreaData] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedAreaTemplate, setSelectedAreaTemplate] = useState(null);
  const [applyingAreaTemplate, setApplyingAreaTemplate] = useState(false);
  const [showAreaTemplateModal, setShowAreaTemplateModal] = useState(false);
  const [planFloors, setPlanFloors] = useState([]);
  const [tempFloors, setTempFloors] = useState([]);
  const [availableFloors, setAvailableFloors] = useState([]);
  const [floorToAdd, setFloorToAdd] = useState(null);
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const [dismissedOptionalCategories, setDismissedOptionalCategories] =
    useState(() => getItemLocal(planDetails?.id) || {});

  const getFloors = () => {
    stateManager.run(async () => {
      const response = await homeService.floors.getAll({
        sort: "displayOrder asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAvailableFloors(response.data);
      }
    });
  };

  const handleAddFloor = () => {
    setTempFloors((prev) => {
      const newFloors = [...prev, floorToAdd].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      return newFloors;
    });
    setFloorToAdd(null);
  };

  const handleOpenAddAreaDrawer = (floor) => {
    setSelectedFloor(floor);
    setShowAreaDrawer(true);
    setAreaData([]);
  };

  const handleCloseAreaDrawer = () => {
    setShowAreaDrawer(false);
    setSelectedAreaTemplate(null);
    setApplyingAreaTemplate(false);
    setShowAreaTemplateModal(false);
    setAreaData([]);
    setSelectedFloor(null);
  };

  const removeFloor = (floor) => {
    stateManager.run(async () => {
      const response = await handleRemoveFloor(floor);
      if (response.status === HTTP_STATUSES.OK) {
        setPlanFloors((prev) => {
          return prev.filter((f) => f !== floor);
        });
        setTempFloors((prev) => {
          return prev.filter((f) => f !== floor);
        });
      }
    });
  };

  const handleAddAreaData = async () => {
    const data = [...areaData];
    await handleAddAreas(data);
    handleCloseAreaDrawer();
  };

  const handleCollapseFloor = (floor) => {
    setCollapsedFloors((prev) => ({
      ...prev,
      [floor]: !prev[floor],
    }));
  };

  const handleDismissCategory = useCallback(
    (categoryId, areaId) => {
      if (!areaId) {
        return;
      }
      const modifiedDismissedCategories = getItemLocal(planDetails?.id) || {};

      if (modifiedDismissedCategories[areaId]) {
        modifiedDismissedCategories[areaId].push(categoryId);
      } else {
        modifiedDismissedCategories[areaId] = [categoryId];
      }

      setDismissedOptionalCategories(modifiedDismissedCategories);
      setItemLocal(planDetails?.id, modifiedDismissedCategories);
    },
    [
      planDetails?.id,
      dismissedOptionalCategories,
      setDismissedOptionalCategories,
    ]
  );

  return {
    showAreaDrawer,
    areaData,
    planFloors,
    tempFloors,
    availableFloors,
    collapsedFloors,
    floorToAdd,
    selectedFloor,
    selectedAreaId,
    selectedAreaTemplate,
    applyingAreaTemplate,
    showAreaTemplateModal,
    dismissedOptionalCategories,
    setAreaData,
    setPlanFloors,
    setTempFloors,
    setFloorToAdd,
    setAvailableFloors,
    setSelectedFloor,
    setSelectedAreaId,
    setSelectedAreaTemplate,
    setShowAreaTemplateModal,
    setApplyingAreaTemplate,
    getFloors,
    removeFloor,
    handleAddFloor,
    handleOpenAddAreaDrawer,
    handleCloseAreaDrawer,
    handleAddAreaData,
    handleCollapseFloor,
    handleDismissCategory,
  };
};
